import { startOfMonth, endOfMonth, addWeeks, addMonths, addDays, isWithinInterval, isBefore, format, differenceInDays } from 'date-fns';

export type RecurringFrequency = 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly' | 'every_4_weeks';

export interface RecurringExpense {
  id: string;
  user_id: string;
  name: string;
  amount: number;
  currency: string;
  category: string;
  frequency: RecurringFrequency;
  next_due_date: string;
  day_of_month?: number | null;
  is_variable_amount: boolean;
  is_auto_paid: boolean;
  reminder_days: number;
  note?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const RECURRING_CATEGORIES = [
  { value: 'subscription', label: 'Subscription', icon: '📺' },
  { value: 'bills', label: 'Bills', icon: '📄' },
  { value: 'rent', label: 'Rent/Housing', icon: '🏠' },
  { value: 'insurance', label: 'Insurance', icon: '🛡️' },
  { value: 'medical', label: 'Medical', icon: '🏥' },
  { value: 'loan', label: 'Loan/Debt', icon: '💳' },
  { value: 'transport', label: 'Transport', icon: '🚗' },
  { value: 'other', label: 'Other', icon: '💰' },
] as const;

export const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'every_4_weeks', label: 'Every 4 weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
] as const;

export const getCategoryIcon = (category: string): string => {
  const found = RECURRING_CATEGORIES.find(c => c.value === category);
  return found?.icon || '💰';
};

export const getCategoryLabel = (category: string): string => {
  const found = RECURRING_CATEGORIES.find(c => c.value === category);
  return found?.label || category;
};

export const getFrequencyLabel = (frequency: string): string => {
  const found = FREQUENCY_OPTIONS.find(f => f.value === frequency);
  return found?.label || frequency;
};

export const getFrequencyShort = (frequency: string): string => {
  switch (frequency) {
    case 'daily': return '/day';
    case 'weekly': return '/week';
    case 'biweekly': return '/2 weeks';
    case 'every_4_weeks': return '/4 weeks';
    case 'monthly': return '/month';
    case 'quarterly': return '/quarter';
    case 'yearly': return '/year';
    default: return '';
  }
};

/**
 * Calculate the next due date based on frequency
 */
export const calculateNextDueDate = (
  currentDueDate: Date,
  frequency: RecurringFrequency
): Date => {
  switch (frequency) {
    case 'daily':
      return addDays(currentDueDate, 1);
    case 'weekly':
      return addWeeks(currentDueDate, 1);
    case 'biweekly':
      return addWeeks(currentDueDate, 2);
    case 'every_4_weeks':
      return addWeeks(currentDueDate, 4);
    case 'monthly':
      return addMonths(currentDueDate, 1);
    case 'quarterly':
      return addMonths(currentDueDate, 3);
    case 'yearly':
      return addMonths(currentDueDate, 12);
    default:
      return addMonths(currentDueDate, 1);
  }
};

/**
 * Calculate how many times a recurring expense occurs in a given month
 */
export const getMonthlyOccurrences = (
  expense: RecurringExpense,
  targetMonth: Date = new Date()
): number => {
  const monthStart = startOfMonth(targetMonth);
  const monthEnd = endOfMonth(targetMonth);
  let occurrences = 0;
  let checkDate = new Date(expense.next_due_date);

  // Move check date backwards until we find the first occurrence before or in the month
  while (isBefore(monthEnd, checkDate)) {
    checkDate = calculateNextDueDate(checkDate, expense.frequency);
    checkDate = addDays(checkDate, -getFrequencyDays(expense.frequency) * 2); // Go back
  }

  // Now move forward and count occurrences within the month
  while (isBefore(checkDate, monthEnd)) {
    if (isWithinInterval(checkDate, { start: monthStart, end: monthEnd })) {
      occurrences++;
    }
    checkDate = calculateNextDueDate(checkDate, expense.frequency);
  }

  // For simplicity, assume 1 occurrence for monthly/quarterly/yearly if the due date falls in this month
  if (occurrences === 0) {
    const nextDue = new Date(expense.next_due_date);
    if (isWithinInterval(nextDue, { start: monthStart, end: monthEnd })) {
      return 1;
    }
  }

  return Math.max(occurrences, 1);
};

const getFrequencyDays = (frequency: RecurringFrequency): number => {
  switch (frequency) {
    case 'daily': return 1;
    case 'weekly': return 7;
    case 'biweekly': return 14;
    case 'every_4_weeks': return 28;
    case 'monthly': return 30;
    case 'quarterly': return 90;
    case 'yearly': return 365;
    default: return 30;
  }
};

/**
 * Calculate total monthly commitment from recurring expenses
 */
export const calculateMonthlyTotal = (
  expenses: RecurringExpense[],
  targetMonth: Date = new Date()
): number => {
  return expenses.filter(e => e.is_active).reduce((total, expense) => {
    const occurrences = getMonthlyEquivalent(expense);
    return total + (expense.amount * occurrences);
  }, 0);
};

/**
 * Get monthly equivalent multiplier for an expense
 */
const getMonthlyEquivalent = (expense: RecurringExpense): number => {
  switch (expense.frequency) {
    case 'daily': return 30; // Average days per month
    case 'weekly': return 4.33; // Average weeks per month
    case 'biweekly': return 2.17;
    case 'every_4_weeks': return 1;
    case 'monthly': return 1;
    case 'quarterly': return 0.33;
    case 'yearly': return 0.083;
    default: return 1;
  }
};

/**
 * Get expenses due within the next N days
 */
export const getUpcomingExpenses = (
  expenses: RecurringExpense[],
  days: number = 7
): RecurringExpense[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const futureDate = addDays(today, days);

  return expenses
    .filter(e => e.is_active)
    .filter(e => {
      const dueDate = new Date(e.next_due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate >= today && dueDate <= futureDate;
    })
    .sort((a, b) => new Date(a.next_due_date).getTime() - new Date(b.next_due_date).getTime());
};

/**
 * Get human-readable due date label
 */
export const getDueDateLabel = (dateString: string): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(dateString);
  dueDate.setHours(0, 0, 0, 0);
  
  const diffDays = differenceInDays(dueDate, today);

  if (diffDays < 0) return 'Overdue';
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays <= 7) return format(dueDate, 'EEEE'); // Day name
  return format(dueDate, 'MMM d');
};

/**
 * Get urgency color class based on due date
 */
export const getDueUrgencyClass = (dateString: string): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(dateString);
  dueDate.setHours(0, 0, 0, 0);
  
  const diffDays = differenceInDays(dueDate, today);

  if (diffDays < 0) return 'text-destructive';
  if (diffDays === 0) return 'text-destructive font-semibold';
  if (diffDays <= 2) return 'text-warning';
  return 'text-muted-foreground';
};
